import React from 'react';
import { InlineMath } from 'react-katex';
import FractionInput from '../../../../components/inputs/FractionInput';
import StatisticsBarChartDiagram from '../../../../components/diagrams/StatisticsBarChartDiagram';
import OpenTaskScoringHint from "../../../../components/ui/OpenTaskScoringHint";


/* ============================================================================
 * Helpers: latex -> number
 * ========================================================================== */

function getRaw(value) {
  if (value == null) return "";

  // event z inputa
  if (typeof value === "object" && value.target && "value" in value.target) {
    return String(value.target.value ?? "");
  }

  // najczęstsze pola z naszych inputów
  if (typeof value === "object") {
    if ("raw" in value && value.raw != null) return String(value.raw);
    if ("latex" in value && value.latex != null) return String(value.latex);
    if ("value" in value && value.value != null && typeof value.value !== "object")
      return String(value.value);
    if ("text" in value && value.text != null) return String(value.text);
    if ("expr" in value && value.expr != null) return String(value.expr);
  }

  return String(value);
}


function mixedToNumber(wholeStr, numStr, denStr) {
  const w = parseInt(wholeStr, 10);
  const n = parseInt(numStr, 10);
  const d = parseInt(denStr, 10);
  if (!d) return null;
  const sign = w < 0 ? -1 : 1;
  return w + sign * (Math.abs(n) / Math.abs(d));
}

// zapis okresowy, np.:
// 2,(6) / 2.(6)
// 2,6(6) / 2.6(6)
// 300,(6)
function repeatingToNumber(str) {
  const s = String(str ?? "").trim().replace(',', '.');
  const m = s.match(/^([+-]?\d+)\.(\d*)\((\d+)\)$/);
  if (!m) return null;

  const intPartStr = m[1];
  const nonRepStr = m[2] || "";
  const repStr = m[3];

  const sign = intPartStr.startsWith('-') ? -1 : 1;
  const intPart = Math.abs(parseInt(intPartStr, 10));
  const nonRep = nonRepStr ? parseInt(nonRepStr, 10) : 0;
  const rep = parseInt(repStr, 10);

  if (!Number.isFinite(intPart) || !Number.isFinite(nonRep) || !Number.isFinite(rep)) {
    return null;
  }

  const k = nonRepStr.length;
  const r = repStr.length;
  const powK = 10 ** k;
  const repDen = 10 ** r - 1;

  return sign * (intPart + nonRep / powK + rep / (powK * repDen));
}

function toNumber(value) {
  const raw0 = getRaw(value);
  if (raw0 == null) return null;
  let raw = raw0.trim();
  if (!raw) return null;

  // obsługa znaku % / \%
  raw = raw.replace(/\\%/g, '%');
  raw = raw.replace(/\s+/g, ' ');
  const percentMatch = raw.match(/^(.+?)\s*%$/);
  if (percentMatch) {
    raw = percentMatch[1].trim();
  }

  const s = raw.replace(',', '.');

  // mieszany latex: k\frac{a}{b}
  let m = s.match(/^(-?\d+)\s*\\frac\{(-?\d+)\}\{(-?\d+)\}$/);
  if (m) {
    return mixedToNumber(m[1], m[2], m[3]);
  }

  // prosty \frac{a}{b}
  const fracLatex = s.match(/^\\frac\{(-?\d+)\}\{(-?\d+)\}$/);
  if (fracLatex) {
    const num = parseFloat(fracLatex[1]);
    const den = parseFloat(fracLatex[2]);
    if (den !== 0) return num / den;
  }

  // mieszany ascii: k a/b
  const mixedAscii = s.match(/^(-?\d+)\s+(-?\d+)\/(-?\d+)$/);
  if (mixedAscii) {
    return mixedToNumber(mixedAscii[1], mixedAscii[2], mixedAscii[3]);
  }

  // prosty ascii: a/b
  const fracAscii = s.match(/^(-?\d+)\/(-?\d+)$/);
  if (fracAscii) {
    const num = parseFloat(fracAscii[1]);
    const den = parseFloat(fracAscii[2]);
    if (den !== 0) return num / den;
  }

  // zapis okresowy (np. 2,(6), 2.(6), 2.6(6))
  const rep = repeatingToNumber(s);
  if (rep != null && Number.isFinite(rep)) {
    return rep;
  }

  // liczba dziesiętna / całkowita
  if (/^-?\d+(\.\d+)?$/.test(s)) {
    return parseFloat(s);
  }

  return null;
}

function numbersEqual(a, b, eps = 1e-6) {
  if (a == null || b == null) return false;
  return Math.abs(a - b) < eps;
}

function gcdInt(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) {
    const t = y;
    y = x % y;
    x = t;
  }
  return x || 1;
}

// num, den – całkowite, już po skróceniu (ale nie musi być)
function mixedFractionLatex(num, den, withPercent = false) {
  const whole = Math.trunc(num / den);
  const rem = Math.abs(num % den);
  const absDen = Math.abs(den);

  let body;
  if (rem === 0) {
    body = String(whole);
  } else if (whole === 0) {
    body = `\\frac{${rem}}{${absDen}}`;
  } else {
    body = `${whole}\\frac{${rem}}{${absDen}}`;
  }

  return withPercent ? `${body}\\%` : body;
}

/**
 * Formatowanie wpisu użytkownika do ładnego LaTeX:
 * - "1/2"      -> "\frac{1}{2}"
 * - "2 3/5"    -> "2\frac{3}{5}"
 * - "125%"     -> "125\%"
 * - "1,5"      -> "1.5"
 * Jeśli user wpisał już LaTeX (np. "\frac{1}{2}"), zostawiamy.
 */
function formatUserLatex(value, withPercent = false) {
  let raw = getRaw(value);
  if (raw == null) raw = '';
  raw = raw.trim();
  if (!raw) {
    return withPercent ? '\\text{–}\\%' : '\\text{–}';
  }

  let s = raw.replace(/\\%/g, '%').trim();
  let hasPercent = false;
  const percentMatch = s.match(/^(.+?)\s*%$/);
  if (percentMatch) {
    hasPercent = true;
    s = percentMatch[1].trim();
  }

  // zamieniamy przecinek na kropkę tylko dla liczb; ułamki "a/b" zostaną bez zmian
  let latex = s;

  // jeśli zaczyna się od "\" zakładamy, że user wpisał już LaTeX
  if (!s.startsWith('\\')) {
    const mixedAscii = s.match(/^(-?\d+)\s+(-?\d+)\/(-?\d+)$/);
    const fracAscii = s.match(/^(-?\d+)\/(-?\d+)$/);

    if (mixedAscii) {
      const whole = mixedAscii[1];
      const num = Math.abs(parseInt(mixedAscii[2], 10));
      const den = Math.abs(parseInt(mixedAscii[3], 10));
      latex = `${whole}\\frac{${num}}{${den}}`;
    } else if (fracAscii) {
      const num = parseInt(fracAscii[1], 10);
      const den = parseInt(fracAscii[2], 10);
      latex = `\\frac{${num}}{${den}}`;
    } else {
      // zwykła liczba / zapis dziesiętny
      latex = s.replace(',', '.');
    }
  }

  if (withPercent || hasPercent) {
    return `${latex}\\%`;
  }
  return latex;
}

/* ============================================================================
 * Scenariusze fabularne
 * ========================================================================== */

const SCENARIOS = [
  {
    id: 'books_students',
    intro:
      'W pewnej klasie odnotowano liczbę książek przeczytanych przez uczniów w jednym miesiącu.',
    xLabel: 'liczba przeczytanych książek',
    yLabel: 'liczba uczniów',
    entityPlural: 'uczniów',
    whatPlural: 'książek',
    valueGenitive: 'przeczytanych książek',
    entityWithValuePrefix: 'uczniów, u których liczba przeczytanych książek',
  },
  {
    id: 'cars_defects',
    intro:
      'W stacji diagnostycznej odnotowywano liczbę usterek wykrytych podczas przeglądów technicznych samochodów.',
    xLabel: 'liczba usterek',
    yLabel: 'liczba samochodów',
    entityPlural: 'samochodów',
    whatPlural: 'usterek',
    valueGenitive: 'usterek',
    entityWithValuePrefix: 'samochodów, w których liczba usterek',
  },
  {
    id: 'goals_matches',
    intro:
      'Podczas turnieju szkolnej ligi piłkarskiej notowano, ile goli strzelały drużyny w pojedynczym meczu.',
    xLabel: 'liczba goli',
    yLabel: 'liczba meczów',
    entityPlural: 'meczów',
    whatPlural: 'goli',
    valueGenitive: 'goli',
    entityWithValuePrefix: 'meczów, w których liczba goli',
  },
];

function pickRandom(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

/* ============================================================================
 * Losowanie rozkładu dla scenariusza
 * ========================================================================== */

/**
 * Profile prawdopodobieństw dla poszczególnych scenariuszy
 * (na kategorie 1..5).
 */
const SCENARIO_PROFILES = {
  books_students: [0.32, 0.28, 0.2, 0.12, 0.08],
  cars_defects: [0.4, 0.3, 0.18, 0.08, 0.04],
  goals_matches: [0.18, 0.26, 0.26, 0.18, 0.12],
};

function normalizeProfile(profile) {
  const sum = profile.reduce((a, b) => a + b, 0);
  if (sum <= 0) return profile.map(() => 1 / profile.length);
  return profile.map((p) => p / sum);
}

/**
 * Generuje liczniki dla kategorii 1..5 dla danego scenariusza:
 * - losujemy całkowitą liczbę przypadków 25–35,
 * - losujemy każdą obserwację wg scenariuszowego profilu prawdopodobieństwa,
 * - dokładamy po 1 sztuce w każdej kategorii, żeby nie było słupka 0.
 */
function generateCountsForScenario(scenarioId) {
  const baseProfile =
    SCENARIO_PROFILES[scenarioId] || [0.3, 0.25, 0.2, 0.15, 0.1];
  const probs = normalizeProfile(baseProfile);

  const cumulative = [];
  let acc = 0;
  for (let i = 0; i < probs.length; i += 1) {
    acc += probs[i];
    cumulative.push(acc);
  }

  const totalEntities = 25 + Math.floor(Math.random() * 11); // 25..35
  const counts = [1, 1, 1, 1, 1]; // minimum po 1 w każdej kategorii
  const remaining = totalEntities - counts.length;

  for (let i = 0; i < remaining; i += 1) {
    const r = Math.random();
    let catIndex = 0;
    while (catIndex < cumulative.length && r > cumulative[catIndex]) {
      catIndex += 1;
    }
    if (catIndex >= counts.length) catIndex = counts.length - 1;
    counts[catIndex] += 1;
  }

  return counts;
}

function hasUniqueMode(counts) {
  const maxCount = Math.max(...counts);
  let howManyMax = 0;
  for (let i = 0; i < counts.length; i += 1) {
    if (counts[i] === maxCount) howManyMax += 1;
  }
  return howManyMax === 1;
}

const COURSE_PART = Object.freeze({ FULL: 'full', STATS: 'stats', PERCENT: 'percent' });

const PERCENT_COMPARISONS = Object.freeze([
  {
    id: 'atLeast2_to_exactly1',
    numeratorLabel: 'co najmniej 2',
    denominatorLabel: 'dokładnie 1',
    numerator: (counts) => counts.slice(1).reduce((sum, value) => sum + value, 0),
    denominator: (counts) => counts[0],
  },
  {
    id: 'exactly3_to_atLeast4',
    numeratorLabel: 'dokładnie 3',
    denominatorLabel: 'co najmniej 4',
    numerator: (counts) => counts[2],
    denominator: (counts) => counts.slice(3).reduce((sum, value) => sum + value, 0),
  },
  {
    id: 'atMost2_to_exactly3',
    numeratorLabel: 'nie więcej niż 2',
    denominatorLabel: 'dokładnie 3',
    numerator: (counts) => counts.slice(0, 2).reduce((sum, value) => sum + value, 0),
    denominator: (counts) => counts[2],
  },
  {
    id: 'exactly2_to_atLeast3',
    numeratorLabel: 'dokładnie 2',
    denominatorLabel: 'co najmniej 3',
    numerator: (counts) => counts[1],
    denominator: (counts) => counts.slice(2).reduce((sum, value) => sum + value, 0),
  },
]);

function getIntegerPercentComparisons(counts) {
  return PERCENT_COMPARISONS.map((comparison) => {
    const numeratorCount = comparison.numerator(counts);
    const denominatorCount = comparison.denominator(counts);
    return { ...comparison, numeratorCount, denominatorCount };
  }).filter(({ numeratorCount, denominatorCount }) => (
    numeratorCount > 0
    && denominatorCount > 0
    && (numeratorCount * 100) % denominatorCount === 0
  ));
}

function getCoursePart(data) {
  return Object.values(COURSE_PART).includes(data?.coursePart)
    ? data.coursePart
    : COURSE_PART.FULL;
}

/* ============================================================================
 * Generator zadania
 * ========================================================================== */

function createOriginalTask() {
  return {
    type: 'MaturaBarChartStatsOpenTask',
    data: {
      scenarioId: 'cars_defects',
      categories: [1, 2, 3, 4, 5],
      counts: [16, 10, 6, 2, 2],
      mode: 1,
      mean: 2,
      meanNum: 2,
      meanDen: 1,
      percent: 125,
      percentNum: 125,
      percentDen: 1,
      total: 36,
      exactlyOne: 16,
      atLeastTwo: 20,
      maxPoints: 3,
      introOverride:
        'W stacji diagnostycznej odnotowywano liczby usterek wykrytych podczas przeglądów technicznych pięcioletnich samochodów w lipcu 2025 roku.',
    },
    source: {
      variant: 'original',
      exam: 'MATURA',
      session: '2025_SIE',
      taskNumber: 30,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === 'original') {
    return createOriginalTask();
  }

  const scenario = pickRandom(SCENARIOS);
  const categories = [1, 2, 3, 4, 5];

  // Losujemy rozkład z jedną dominantą i całkowitym wynikiem procentowym.
  let counts = null;
  let availableComparisons = [];
  for (let attempt = 0; attempt < 240; attempt += 1) {
    const candidate = generateCountsForScenario(scenario.id);
    const candidateComparisons = getIntegerPercentComparisons(candidate);
    if (hasUniqueMode(candidate) && candidateComparisons.length > 0) {
      counts = candidate;
      availableComparisons = candidateComparisons;
      break;
    }
  }
  if (!counts) {
    counts = [16, 10, 6, 2, 2];
    availableComparisons = getIntegerPercentComparisons(counts);
  }

  const total = counts.reduce((a, b) => a + b, 0);
  const weightedSum = categories.reduce(
    (acc, cat, idx) => acc + cat * counts[idx],
    0
  );

  // średnia – jako liczba i jako ułamek
  const meanNumRaw = weightedSum;
  const meanDenRaw = total;
  const gMean = gcdInt(meanNumRaw, meanDenRaw);
  const meanNum = meanNumRaw / gMean;
  const meanDen = meanDenRaw / gMean;
  const mean = meanNumRaw / meanDenRaw;

  // dominanta – mamy gwarancję, że jest tylko jedna
  const maxCount = Math.max(...counts);
  const modeIndex = counts.indexOf(maxCount);
  const modeValue = categories[modeIndex];

  const comparison = pickRandom(availableComparisons);
  const exactlyOne = counts[0];
  const atLeastTwo = total - exactlyOne;
  const percentNumRaw = comparison.numeratorCount * 100;
  const percentDenRaw = comparison.denominatorCount;
  const gPerc = gcdInt(percentNumRaw, percentDenRaw);
  const percentNum = percentNumRaw / gPerc;
  const percentDen = percentDenRaw / gPerc;
  const percent = percentNumRaw / percentDenRaw;

  const coursePart = generationOptions.coursePart === COURSE_PART.STATS
    ? COURSE_PART.STATS
    : Object.prototype.hasOwnProperty.call(generationOptions, 'courseDraw')
      ? COURSE_PART.STATS
      : COURSE_PART.FULL;

  return {
    type: 'MaturaBarChartStatsOpenTask',
    data: {
      scenarioId: scenario.id,
      categories,
      counts,
      mode: modeValue,
      mean,
      meanNum,
      meanDen,
      percent,
      percentNum,
      percentDen,
      total,
      exactlyOne,
      atLeastTwo,
      comparisonId: comparison.id,
      comparisonNumeratorLabel: comparison.numeratorLabel,
      comparisonDenominatorLabel: comparison.denominatorLabel,
      comparisonNumeratorCount: comparison.numeratorCount,
      comparisonDenominatorCount: comparison.denominatorCount,
      coursePart,
      maxPoints: coursePart === COURSE_PART.STATS ? 2 : 3,
    },
  };
}

function getScenarioById(id) {
  return SCENARIOS.find((s) => s.id === id) || SCENARIOS[0];
}

function ComparisonLabel({ label }) {
  const value = String(label || '').trim();
  const match = value.match(/^(.*?)(-?\d+(?:[.,]\d+)?)$/);
  if (!match) return <>{value}</>;

  const text = match[1].trimEnd();
  const numberLatex = match[2].replace(',', '{,}');
  return <>{text}{text ? ' ' : null}<InlineMath math={numberLatex} /></>;
}

/* ============================================================================
 * Treść zadania
 * ========================================================================== */

export function renderTask({ data }) {
  const { scenarioId, categories, counts } = data;
  const scenario = getScenarioById(scenarioId);
  const intro = data.introOverride || scenario.intro;
  const coursePart = getCoursePart(data);
  const showStats = coursePart !== COURSE_PART.PERCENT;
  const showPercent = coursePart !== COURSE_PART.STATS;
  const maxScore = coursePart === COURSE_PART.FULL ? 3 : coursePart === COURSE_PART.STATS ? 2 : 1;
  const numeratorLabel = data.comparisonNumeratorLabel || 'co najmniej 2';
  const denominatorLabel = data.comparisonDenominatorLabel || 'dokładnie 1';

  return (
    <div className="space-y-3" style={{ textAlign: 'left' }}>
      <p>
        <strong>Zadanie maturalne – otwarte (0–{maxScore} pkt).</strong>
      </p>

      <p>
        {intro} Wszystkie odnotowane wyniki przedstawiono na
        poniższym diagramie.
      </p>

      <StatisticsBarChartDiagram
        categories={categories}
        counts={counts}
        xLabel={scenario.xLabel}
        yLabel={scenario.yLabel}
      />

      <p>
        Uzupełnij zdania. Wpisz odpowiednie liczby w wykropkowanych miejscach,
        aby zdania były prawdziwe.
      </p>

      <ol className="list-decimal ml-6 space-y-1">
        {showStats ? <li>
          Dominanta liczby {scenario.valueGenitive} jest równa{' '}
          <InlineMath math={'\\_\\_\\_\\_'} /> .
        </li> : null}
        {showStats ? <li>
          Średnia arytmetyczna liczby {scenario.valueGenitive} jest równa{' '}
          <InlineMath math={'\\_\\_\\_\\_'} /> .
        </li> : null}
        {showPercent ? <li value={showStats ? 3 : 1}>
          Liczba {scenario.entityWithValuePrefix} wyniosła{' '}
          <ComparisonLabel label={numeratorLabel} />, stanowi{' '}
          <InlineMath math={'\\_\\_\\_\\_'} /> procent liczby{' '}
          {scenario.entityWithValuePrefix} wyniosła{' '}
          <ComparisonLabel label={denominatorLabel} />.
        </li> : null}
      </ol>
    </div>
  );
}

/* ============================================================================
 * Input użytkownika – FractionInput
 * ========================================================================== */

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const ua = userAnswer || {};
  const coursePart = getCoursePart(data);
  const showStats = coursePart !== COURSE_PART.PERCENT;
  const showPercent = coursePart !== COURSE_PART.STATS;
  const setField =
    (key) =>
    (val) =>
      onChange({ ...(ua || {}), [key]: val });

  return (
    <div style={{ display: 'grid', rowGap: 16, maxWidth: 420 }}>
      {coursePart !== COURSE_PART.PERCENT ? <OpenTaskScoringHint variant="independent" /> : null}
      {showStats ? <div style={{ display: 'grid', rowGap: 4 }}>
        <div>
          <strong>1.</strong> Dominanta (wpisz liczbę):
        </div>
        <FractionInput
          value={ua.d1}
          onChange={setField('d1')}
          disabled={disabled}
          placeholder="np. 1"
          label=""
        />
      </div> : null}

      {showStats ? <div style={{ display: 'grid', rowGap: 4 }}>
        <div>
          <strong>2.</strong> Średnia arytmetyczna:
        </div>
        <FractionInput
          value={ua.d2}
          onChange={setField('d2')}
          disabled={disabled}
          placeholder="np. 2 lub 5/4"
          label=""
        />
      </div> : null}

      {showPercent ? <div style={{ display: 'grid', rowGap: 4 }}>
        <div>
          {coursePart === COURSE_PART.FULL ? <><strong>3.</strong>{' '}</> : null}Procent:
        </div>
        <FractionInput
          value={ua.d3}
          onChange={setField('d3')}
          disabled={disabled}
          placeholder="np. 125 lub 125%"
          label=""
          suffixLatex="\%"
        />
      </div> : null}
    </div>
  );
}

/* ============================================================================
 * Sprawdzanie (0–3 pkt)
 * ========================================================================== */

export function checkAnswer({ data }, userAnswer) {
  const ua = userAnswer || {};
  const { mode, mean, percent } = data;
  const coursePart = getCoursePart(data);

  const d1Val = toNumber(ua.d1);
  const d2Val = toNumber(ua.d2);
  const d3Val = toNumber(ua.d3);

  const d1Ok = numbersEqual(d1Val, mode);

  // średnia – dopuszczamy odchylenie do 0.01
  let d2Ok = false;
  if (d2Val != null && !Number.isNaN(d2Val)) {
    const diff = Math.abs(d2Val - mean);
    d2Ok = diff <= 0.01 + 1e-9;
  }

  // procent – dopuszczamy odchylenie do 1 pp
  let d3Ok = false;
  if (d3Val != null && !Number.isNaN(d3Val)) {
    const diff = Math.abs(d3Val - percent);
    d3Ok = diff <= 1 + 1e-9;
  }

  const checkedParts = coursePart === COURSE_PART.STATS
    ? [d1Ok, d2Ok]
    : coursePart === COURSE_PART.PERCENT
      ? [d3Ok]
      : [d1Ok, d2Ok, d3Ok];
  const score = checkedParts.filter(Boolean).length;
  const maxScore = checkedParts.length;

  const parts = coursePart === COURSE_PART.STATS
    ? { "1) dominanta": d1Ok, "2) średnia": d2Ok }
    : coursePart === COURSE_PART.PERCENT
      ? { procent: d3Ok }
      : {
          "1) dominanta": d1Ok,
          "2) średnia": d2Ok,
          "3) procent": d3Ok,
        };

  return {
    isCorrect: score === maxScore,
    correct: score === maxScore,
    score,
    maxScore,
    parts,
  };
}

/* ============================================================================
 * Poprawna odpowiedź
 * ========================================================================== */

export function renderAnswer({ data }) {
  const { mode, meanNum, meanDen, percentNum, percentDen } = data;
  const coursePart = getCoursePart(data);
  const showStats = coursePart !== COURSE_PART.PERCENT;
  const showPercent = coursePart !== COURSE_PART.STATS;

  const meanLatex = mixedFractionLatex(meanNum, meanDen, false);
  const percentLatex = mixedFractionLatex(percentNum, percentDen, true);

  return (
    <div style={{ marginTop: 8, display: 'grid', rowGap: 4 }}>
      {showStats ? <div>
        <strong>1.</strong> <InlineMath math={String(mode)} />
      </div> : null}
      {showStats ? <div>
        <strong>2.</strong> <InlineMath math={meanLatex} />
      </div> : null}
      {showPercent ? <div>
        {showStats ? <><strong>3.</strong>{' '}</> : null}
        <InlineMath math={percentLatex} />
      </div> : null}
    </div>
  );
}

/* ============================================================================
 * Podgląd odpowiedzi użytkownika
 * ========================================================================== */

export function renderUserAnswer(userAnswer, task) {
  const coursePart = getCoursePart(task?.data);
  const showStats = coursePart !== COURSE_PART.PERCENT;
  const showPercent = coursePart !== COURSE_PART.STATS;
  const visibleKeys = coursePart === COURSE_PART.STATS
    ? ["d1", "d2"]
    : coursePart === COURSE_PART.PERCENT
      ? ["d3"]
      : ["d1", "d2", "d3"];

  const isCompletelyEmpty = () => {
    if (!userAnswer) return true;
    return visibleKeys.every((key) => {
      const v = userAnswer[key];
      if (v == null) return true;
      const raw =
        typeof v === "object" && v.raw != null ? String(v.raw) : String(v);
      return raw.trim() === "";
    });
  };

  if (isCompletelyEmpty()) {
    return <i>(brak odpowiedzi)</i>;
  }

  const { d1, d2, d3 } = userAnswer;

  const latexOrDash = (field, withPercent = false) =>
    formatUserLatex(field, withPercent);

  return (
    <div style={{ marginTop: 8, display: "grid", rowGap: 4 }}>
      {showStats ? <div>
        <strong>1.</strong> <InlineMath math={latexOrDash(d1)} />
      </div> : null}
      {showStats ? <div>
        <strong>2.</strong> <InlineMath math={latexOrDash(d2)} />
      </div> : null}
      {showPercent ? <div>
        {showStats ? <><strong>3.</strong>{' '}</> : null}
        <InlineMath math={latexOrDash(d3, true)} />
      </div> : null}
    </div>
  );
}


/* ============================================================================
 * Wyjaśnienie krok po kroku
 * ========================================================================== */

export function renderExplanation({ data }) {
  const {
    scenarioId,
    categories,
    counts,
    mode,
    mean,
    meanNum,
    meanDen,
    percentNum,
    percentDen,
    total,
    exactlyOne,
    atLeastTwo,
    comparisonNumeratorLabel,
    comparisonDenominatorLabel,
    comparisonNumeratorCount,
    comparisonDenominatorCount,
  } = data;
  const scenario = getScenarioById(scenarioId);
  const coursePart = getCoursePart(data);
  const showStats = coursePart !== COURSE_PART.PERCENT;
  const showPercent = coursePart !== COURSE_PART.STATS;

  const pairs = categories.map((cat, i) => ({
    cat,
    count: counts[i],
  }));

  const weightedSum = categories.reduce(
    (acc, cat, idx) => acc + cat * counts[idx],
    0
  );

  const exactPercentLatex = mixedFractionLatex(percentNum, percentDen, true);
  const numeratorLabel = comparisonNumeratorLabel || 'co najmniej 2';
  const denominatorLabel = comparisonDenominatorLabel || 'dokładnie 1';
  const numeratorCount = comparisonNumeratorCount ?? atLeastTwo;
  const denominatorCount = comparisonDenominatorCount ?? exactlyOne;

  const exactMeanLatex = mixedFractionLatex(meanNum, meanDen, false);
  const meanRounded2 = Math.round(mean * 100) / 100;
  const meanRounded2Str = meanRounded2.toFixed(2).replace('.', '{,}');

  return (
    <div className="space-y-3" style={{ textAlign: 'left' }}>
      {showStats ? <>
        <p>
          <strong>Krok 1.</strong> Z diagramu odczytujemy, ile{' '}
          {scenario.entityPlural} odpowiada każdej liczbie {scenario.whatPlural}.
        </p>

        <ul className="list-disc ml-6">
          {pairs.map(({ cat, count }) => (
            <li key={cat}>
              liczba {scenario.whatPlural}:{' '}
              <InlineMath math={String(cat)} /> – liczba{' '}
              {scenario.entityPlural}: <InlineMath math={String(count)} />
            </li>
          ))}
        </ul>

        <p>
          Dominanta to wartość, która występuje najczęściej – tutaj jest nią{' '}
          <InlineMath math={String(mode)} />.
        </p>

        <p>
          <strong>Krok 2.</strong> Obliczamy średnią arytmetyczną liczby{' '}
          {scenario.valueGenitive}: najpierw liczymy łączną liczbę{' '}
          {scenario.valueGenitive} (wartość razy liczebność), a potem dzielimy
          przez łączną liczbę {scenario.entityPlural}.
        </p>

        <div style={{ marginTop: 6 }}>
          <InlineMath
            math={`${categories.map((cat, idx) => `${cat}\\cdot ${counts[idx]}`).join(' + ')} = ${weightedSum}.`}
          />
        </div>

        <div style={{ marginTop: 6 }}>
          <InlineMath math={`${counts.join(' + ')} = ${total}.`} />
        </div>

        <p>Zatem:</p>
        <InlineMath
          math={`\\bar{x} = \\dfrac{${weightedSum}}{${total}} = ${exactMeanLatex} \\approx ${meanRounded2Str}.`}
        />
      </> : null}

      {showPercent ? <>
        <p>
          <strong>Krok {showStats ? 3 : 1}.</strong> Obliczamy, jaki procent liczby{' '}
          {scenario.entityPlural} stanowią te przypadki, w których liczba{' '}
          {scenario.valueGenitive} spełniła warunek „<ComparisonLabel label={numeratorLabel} />”.
        </p>

        <p>
          Warunek „<ComparisonLabel label={numeratorLabel} />” spełnia <InlineMath math={String(numeratorCount)} /> przypadków,
          natomiast warunek „<ComparisonLabel label={denominatorLabel} />” spełnia{' '}
          <InlineMath math={String(denominatorCount)} /> przypadków.
        </p>

        <p><strong>Krok {showStats ? 4 : 2}.</strong> Liczbę przypadków z pierwszej grupy dzielimy przez liczbę przypadków z grupy odniesienia. To ta druga liczba stanowi tutaj 100%, a nie suma wszystkich słupków.</p>
        <InlineMath math={`p = \\frac{${numeratorCount}}{${denominatorCount}}\\cdot 100\\%`} />
        <p><strong>Krok {showStats ? 5 : 3}.</strong> Obliczamy iloraz i mnożymy go przez 100%. Wynik może przekroczyć 100%, jeżeli porównywana grupa jest większa od grupy odniesienia.</p>
        <InlineMath math={`p = ${exactPercentLatex}`} />

        <p>
          Wynik jest liczbą całkowitą, więc nie trzeba go zaokrąglać.
        </p>
      </> : null}
    </div>
  );
}
